import { type CallhomeSiteWithRelationalData, type SiteId } from '@solinkcorp/api-types';
import { useStaleSWRConfig } from '@solinkcorp/utils/hooks';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import useSWR, { type SWRConfiguration } from 'swr';
import { useCallhomeClient } from '../context';
const SITE_KEY = 'site';

export const getSiteKey = (siteId: SiteId) => [SITE_KEY, siteId];

export default function useSite({
  siteId,
  config,
  disabled,
}: {
  siteId: SiteId | undefined;
  config?: SWRConfiguration;
  disabled?: boolean;
}) {
  const callhomeClient = useCallhomeClient();
  const shouldFetch = siteId && siteId !== ALL_LOCATIONS && !disabled;

  const swrKey = shouldFetch ? getSiteKey(siteId) : null;
  const swrDefaultConfig = useStaleSWRConfig(swrKey);

  const siteResponse = useSWR(
    swrKey,
    async ([, id]) => {
      try {
        const res = await callhomeClient.getSite<CallhomeSiteWithRelationalData>(id as SiteId, {
          filter: JSON.stringify({
            include: [
              { relation: 'alarmContacts' },
              { relation: 'alarmConfig' },
              {
                relation: 'devices',
                scope: {
                  fields: [
                    'id',
                    'cloudId',
                    'resellerId',
                    'customerId',
                    'siteId',
                    'address',
                    'name',
                    'overrideName',
                    'state',
                    'nextCheckin',
                    'lastCheckin',
                    'currentSoftwareMajorVersion',
                    'currentSoftwareVersion',
                    'ipAddress',
                    'useLocalIpAsPrimary',
                    'cloudOnly',
                    'speedTestUploadRateS3',
                    'latestSpeedTestResults',
                    'isVirtual',
                    'connectSettings',
                    'deviceUrl',
                    'deviceUrlV2',
                    'deviceLocalUrl',
                    'metadata',
                    'features',
                    'turnUrl',
                    'availablePlaybackMethods',
                    'tags',
                    'status',
                    'type',
                    'tpuStatus',
                    'timezoneId',
                  ],
                },
                include: ['operatingHoursSchedule'],
              },
              {
                relation: 'cameras',
                scope: {
                  fields: [
                    'id',
                    'cloudId',
                    'resellerId',
                    'customerId',
                    'siteId',
                    'deviceId',
                    'cameraId',
                    'overrideName',
                    'name',
                    'geolocation',
                    'streams',
                    'status',
                    'deviceUrl',
                    'deviceLocalUrl',
                    'thumbnail',
                    'ip',
                    'isMotionEnabled',
                    'manufacturer',
                    'model',
                    'firmwareVersion',
                    'serialNumber',
                    'identifiedBy',
                    'isMotionAlarmEnabled',
                    'ai',
                    'audioParams',
                    'annotations',
                    'motionParams',
                    '360Settings',
                    'segmentThumbnail',
                    'cameraTagIds',
                    'regions',
                    'vmsName',
                    'access',
                    'hardwareId',
                    'isUnplugged',
                    'isOutOfFocus',
                    'createdAt',
                    'motionAlarmScheduleId',
                    'turnUrl',
                  ],
                  include: ['motionAlarmSchedule'],
                },
              },
              { relation: 'motionAlarmSchedule' },
              {
                relation: 'posDevices',
                fields: ['id', 'siteId', 'deviceId', 'name'],
              },
              { relation: 'cameraLinks' },
            ],
          }),
        });
        return res;
      } catch (_error) {
        throw new Error('Failed to fetch site');
      }
    },
    config ?? swrDefaultConfig,
  );

  return {
    site: siteResponse.data,
    mutate: siteResponse.mutate,
    isLoading: siteResponse.isLoading,
    error: siteResponse.error,
  };
}
